
import { ManagersCountryBudget } from 'nomst/Stores/Budget/countryBudget';
import { IManagersCountryBudget } from 'typings/managersCountryBudget';
import { ManagerBudget } from 'nomst/Stores/Budget/managerBudget';
import { CycleBudget } from 'nomst/Stores/Configuration/cycleBudget';
import { EmployeeCountrySummary } from 'nomst/Stores/Budget/employeeCountrySummary';
import { Percentage } from 'nomst/Stores/Budget/percentage';
import { NumberValue } from 'nomst/Stores/Utility/numberValue';
import { AmountCode, BudgetGroupingOptions } from 'typings/enums';
import { ProrationRule } from 'nomst/Stores/Configuration/prorationRule';
import { dataManagerFactory, configurationsStore, currencyConversion } from 'utils/MobxDataManager/singletons';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { DefaultHomeCurrency } from 'nomst/Stores/Utility/defaultCurrency';
import { IGroupedBudget } from 'typings/groupedBudget';
import { observable, computed, action, makeObservable } from 'mobx';
import { sleep } from 'utils/timeoutUtils';

export class EditBudgetViewStore {
    @observable selectedCountryCode: string;
    @observable availableEmployeeCountrySummary: EmployeeCountrySummary[];
    @observable countryBudget: Nullable<ManagersCountryBudget>;
    @observable isLoading: boolean;
    @observable budgetPercentage: Percentage;
    @observable budgetValue: NumberValue;
    @observable useProration: boolean;
    @observable shouldShowSyncWithLabDialog: Nullable<boolean>;
    @observable unconfirmedUseAutoSync: boolean;
    @observable currentBudget: CycleBudget;
    @observable departmentNames: Set<string>;
    @observable businessUnits: Set<string>;
    @observable workLocations: Set<string>;
    @observable selectedGroupByOption: string;
    @observable groupedBudgets: IGroupedBudget;
    @observable groupedSummaryBudgets: ManagersCountryBudget[];  

    constructor() {
        makeObservable(this);
        this.selectedCountryCode = '';
        this.availableEmployeeCountrySummary = [];
        this.countryBudget = null;
        this.budgetPercentage = new Percentage(this.currentConfiguration.format);
        this.budgetValue = new NumberValue(this.currentConfiguration.format);
        this.useProration = false;
        this.shouldShowSyncWithLabDialog = false;

        this.setBudgetPercentage = this.setBudgetPercentage.bind(this);
        this.setBudgetValue = this.setBudgetValue.bind(this);

        this.budgetPercentage.setOnChangeCallback(this.setBudgetPercentage);
        this.budgetValue.setOnChangeCallback(this.setBudgetValue);
        this.departmentNames = new Set<string>();
        this.businessUnits = new Set<string>();
        this.workLocations = new Set<string>();
        this.selectedGroupByOption = BudgetGroupingOptions.All;
        this.groupedSummaryBudgets = [];
    }

    @computed
    get currentConfiguration(): Configuration {
        return configurationsStore.currentConvertedConfiguration;
    }

    @computed
    get availableCountryOptions(): Array<{ text: string, value: string }> {
        return this.availableEmployeeCountrySummary.map(employeeCountrySummary => ({
            text: `${employeeCountrySummary.name} (${employeeCountrySummary.employeeCount})`,
            value: employeeCountrySummary.code
        }));
    }

    
    @computed
    get availableGroupByOptions(): IOption[] {
        const budgetGroupingOptions: IOption[] = [];

        budgetGroupingOptions.push(
            {
                text: 'All',
                value: 'All'
            });

        if(this.departmentNames.size > 0) {
            budgetGroupingOptions.push(
                {
                    text: 'Department',
                    value: BudgetGroupingOptions.Department
                });
        }

        if(this.businessUnits.size > 0) {
            budgetGroupingOptions.push(
                {
                    text: 'BusinessUnit',
                    value: BudgetGroupingOptions.BusinessUnit
                });
        }

        if(this.workLocations.size > 0) {
            budgetGroupingOptions.push(
                {
                    text: 'WorkLocation',
                    value: BudgetGroupingOptions.WorkLocation
                });
        }

        return budgetGroupingOptions;
    }

    @computed
    get selectedEmployeeCountrySummary(): EmployeeCountrySummary {
        return this.availableEmployeeCountrySummary.find(employeeCountrySummary =>
            employeeCountrySummary.code === this.selectedCountryCode) ?? {} as EmployeeCountrySummary;
    }

    @action
    setCurrentBudget(budget: CycleBudget): void {
        this.currentBudget = budget;
    }

    @action
    setSelectGroupByOption(value: string): void {
        this.selectedGroupByOption = value;
        this.updateGroupings();
    }

    shouldDisableInput(budget: CycleBudget): boolean {
        return this.currentConfiguration.isClosing
            || this.currentConfiguration.isClosed
            || budget.autoSync;
    }

    targetBudgetLabelText(budget: CycleBudget): string {
        if (budget.isStockBudgetInUnitOnly) {
            return '# of Share Stock Target';
        }

        return 'Target %';
    }

    shouldShowOriginalBudget(budget: CycleBudget): boolean {
        if (this.useProration) {
            return false;
        }

        return budget.cycleBudgetFields.some(budgetField => budgetField.shouldShowOriginalBudget);
    }

    checkIfBudgetHasValidProration(budget: CycleBudget): boolean {
        let budgetProrationRule: ProrationRule|null = null;
        for (const cycleAmount of budget.cycleBudgetFields) {
            if (cycleAmount.prorationRule && !budgetProrationRule) {
                budgetProrationRule = new ProrationRule(cycleAmount.prorationRule);
                continue;
            }

            if (cycleAmount.prorationRule &&
                cycleAmount.prorationRule.prorationRuleId !== budgetProrationRule?.prorationRuleId) {
                return false;
            }
        }

        return budgetProrationRule !== null;
    }

    shouldShowTotalCurrentPay(budget: CycleBudget): boolean {
        return budget.isStockBudgetInUnitOnly === false;
    }

    shouldShowAdjustedTargetPercentage(budget: CycleBudget): boolean {
        return budget.isStockBudgetInUnitOnly === false;
    }

    shouldShowMultiCurrencyDropdown(budget: CycleBudget): boolean {
        return this.currentConfiguration.hasMultipleCurrency && budget.isStockBudgetInUnitOnly === false;
    }

    showGroupByOption(): boolean {
        return !!this.countryBudget?.managers.length;
    }

    shouldShowImportFromLabButton(budget: CycleBudget): boolean {
        return budget.cycleBudgetFields.some(budgetField =>
            budgetField.amountCode === AmountCode.Merit ||
            budgetField.amountCode === AmountCode.Bonus ||
            budgetField.amountCode === AmountCode.VariablePay ||
            budgetField.amountCode === AmountCode.Stock);
    }

    @action
    async fetchBudgetSummaryAllCountries(budget: CycleBudget): Promise<void> {
        this.isLoading = true;
        await dataManagerFactory.getManagerDataManager().getManagerBudgets(
            this.currentConfiguration.masterAccountId,
            budget.cycleId,
            budget.budgetCycleId) as IManagerBudget[];
        
        await this.pollBudgetSummaryAllCountries(budget);
    }

    @action
    async pollBudgetSummaryAllCountries(budget: CycleBudget): Promise<void> {
        let counter = 0;
        let managerBudgets: IManagerBudget[]|null = [];

        while(counter++ < 100) {
            managerBudgets =
            await dataManagerFactory.getManagerDataManager().pollManagerBudgets(
                this.currentConfiguration.masterAccountId,
                budget.cycleId,
                budget.budgetCycleId) as IManagerBudget[];
            if (!managerBudgets) {
                await sleep(3000);
            } else {
                break;
            }
        }

        if (!managerBudgets?.length) {
            return;
        }

        const initManagerBudgets: ManagerBudget[] = managerBudgets?.map(budget =>
            new ManagerBudget(budget, this.currentConfiguration.format, this.currentBudget));

        const countryBudget = {
            countryCode: '',
            isoCurrencyCode: DefaultHomeCurrency,
            isUnitValue: budget.isStockBudgetInUnitOnly,
            managers: initManagerBudgets
        } as IManagersCountryBudget;

        countryBudget.managers.forEach(manager => {
            manager.isUnitValue = budget.isStockBudgetInUnitOnly;
        });

        this.countryBudget = new ManagersCountryBudget(countryBudget, this.currentConfiguration.format);
        this.departmentNames = this.getDepartmentNames();
        this.workLocations = this.getWorkLocations();
        this.businessUnits = this.getBusinessUnits();
        this.updateGroupings();
        this.isLoading = false;
    }

    @action
    async fetchBudgetSummaryByCountry(countryCode: string, budget: CycleBudget): Promise<void> {
        this.isLoading = true;
        const countryBudget = {
            countryCode: this.selectedEmployeeCountrySummary.code ?? countryCode,
            isoCurrencyCode: this.selectedEmployeeCountrySummary.isoCurrencyCode ?? DefaultHomeCurrency
        } as IManagersCountryBudget;

        await dataManagerFactory.getManagerDataManager().getManagerBudgets(
            this.currentConfiguration.masterAccountId,
            budget.cycleId,
            budget.budgetCycleId,
            countryCode) as ManagerBudget[];
            
        await this.pollBudgetSummaryByCountry(budget, countryBudget);
    }

    @action
    async pollBudgetSummaryByCountry(budget: CycleBudget, countryBudget: IManagersCountryBudget): Promise<void> {
        let counter = 0;
        let managerBudgets: IManagerBudget[]|null = [];

        while(counter++ < 100) {
            managerBudgets =
            await dataManagerFactory.getManagerDataManager().pollManagerBudgets(
                this.currentConfiguration.masterAccountId,
                budget.cycleId,
                budget.budgetCycleId) as IManagerBudget[];
            if (!managerBudgets) {
                await sleep(3000);
            } else {
                break;
            }
        }

        if (!managerBudgets?.length) {
            return;
        }
        const initManagerBudgets: ManagerBudget[] = managerBudgets.map(budget =>
            new ManagerBudget(budget, this.currentConfiguration.format, this.currentBudget));

        countryBudget.isUnitValue = budget.isStockBudgetInUnitOnly;
        countryBudget.managers = initManagerBudgets;
        countryBudget.managers.forEach(manager => {
            manager.isUnitValue = budget.isStockBudgetInUnitOnly;
        });

        this.countryBudget = new ManagersCountryBudget(countryBudget, this.currentConfiguration.format);
        this.departmentNames = this.getDepartmentNames();
        this.workLocations = this.getWorkLocations();
        this.businessUnits = this.getBusinessUnits();
        this.updateGroupings();
        this.isLoading = false;
    }

    @action
    async fetchEmployeeCountrySummaries(budget: CycleBudget): Promise<void> {
        this.isLoading = true;
        this.selectedCountryCode = '';
        const employeeDataManager = dataManagerFactory.getEmployeeDataManager();
        this.availableEmployeeCountrySummary = await employeeDataManager
            .getEmployeeCountrySummaries(this.currentConfiguration.masterAccountId, budget.cycleId) as EmployeeCountrySummary[];

        if (this.availableEmployeeCountrySummary[0]) {
            this.selectedCountryCode = this.availableEmployeeCountrySummary[0].code;
        }

        this.isLoading = false;
    }

    @action
    async applyBudgets(budget: CycleBudget): Promise<void> {
        this.isLoading = true;
        await dataManagerFactory.getBudgetServiceMaanger().updateBudgetProration(
            this.currentConfiguration.masterAccountId,
            this.currentConfiguration.cycleId,
            budget.budgetCycleId,
            this.useProration);

        budget.updateProration(this.useProration);

        const updatedManagerBudgets = this.countryBudget?.managers.filter(
            managerBudget => managerBudget.initialAdjustedBudget !== managerBudget.adjustedBudget);
        if (updatedManagerBudgets && updatedManagerBudgets.length > 0 && this.countryBudget) {
            await dataManagerFactory.getManagerDataManager().applyBudgets(
                this.currentConfiguration.masterAccountId,
                this.currentConfiguration.cycleId,
                budget.budgetCycleId,
                updatedManagerBudgets,
                this.countryBudget.countryCode);
        }

        this.isLoading = false;
    }

    @action
    setBudgetPercentage(percentage: number): void {
        this.countryBudget?.managers.forEach((manager) => {
            manager.changeBudgetByPercentage(percentage, true);
        });
    }

    @action
    setBudgetValue(newValue: number): void {
        this.countryBudget?.managers.forEach((manager) => {
            manager.changeBudgetByValue(newValue, true);
        });
    }

    exportCountryBudgetToCsv(budgetCycleId: number, name: string): Promise<void> {
        const { multiCountry } = this.currentConfiguration.teamPageViews;
        const sourceCountryCode = multiCountry ? this.selectedCountryCode : '';
        // when multiCountry off, only targetCurrency exists
        // when multiCountry on, both countryCode and targetCurrency exists.
        const targetCurrency = currencyConversion.selectedCurrency || '';
        const getAll =  (multiCountry && targetCurrency === '' && sourceCountryCode === '')
                        || (targetCurrency === '');
        const csvFileName = `Budget-${name}.csv`;
        return dataManagerFactory.getBudgetServiceMaanger()
            .exportCountryBudgetToCsv(
                this.currentConfiguration.masterAccountId,
                this.currentConfiguration.cycleId,
                budgetCycleId,
                sourceCountryCode,
                targetCurrency,
                multiCountry,
                getAll,
                csvFileName,
                this.selectedGroupByOption);
    }

    @action
    showSyncWithLabDialog(): void {
        this.shouldShowSyncWithLabDialog = true;
    }

    @action
    async setBudgetAutoSyncAsync(budget: CycleBudget): Promise<void> {
        this.isLoading = true;
        await dataManagerFactory.getBudgetServiceMaanger().setBudgetAutoSyncAsync(
            this.currentConfiguration.masterAccountId,
            this.currentConfiguration.cycleId,
            budget.budgetCycleId,
            budget.autoSync);
        
        configurationsStore.clearCycleconfigurations();
        await configurationsStore.fetchCycleConfigurations();

        this.isLoading = false;
    }

    @action
    changeEmployeeCountrySummary(countryCode: string): void {
        this.selectedCountryCode = countryCode;
    }

    @action
    setInitialUseProration(useProration: boolean): void {
        this.useProration = useProration;
    }

    @action
    toggleUseProration(useProration: boolean): void {
        this.useProration = useProration;
        if (!this.countryBudget) {
            return;
        }

        this.countryBudget.managers.forEach((manager) => {
            if (useProration) {
                manager.adjustBudgetToUseProration();
                return;
            }

            manager.adjustBudgetToNotUseProration();
        });
    }

    @action
    handleSetSyncWithLab(unconfirmedUseAutoSync: boolean): void {
        this.unconfirmedUseAutoSync = unconfirmedUseAutoSync;
        this.showSyncWithLabDialog();
    }

    @action
    hideSyncWithLabDialog(): void {
        this.shouldShowSyncWithLabDialog = false;
    }

    @action
    resetView(): void {
        this.selectedCountryCode = '';
        this.availableEmployeeCountrySummary = [];
        this.countryBudget = null;
        this.budgetPercentage = new Percentage(this.currentConfiguration.format);
        this.budgetValue = new NumberValue(this.currentConfiguration.format);
        this.budgetPercentage.setOnChangeCallback(this.setBudgetPercentage);
        this.budgetValue.setOnChangeCallback(this.setBudgetValue);
        this.useProration = false;
    }

    @action
    updateGroupings(): void {
        this.groupedBudgets = this.groupBudgetsByKey(this.selectedGroupByOption);
        const groupedSummaryBudgets: ManagersCountryBudget[] = [];
        Object.entries(this.groupedBudgets).map((groupedBudgetEntry) => {
            const groupedSummaryBudgetDto = {
                countryCode: this.countryBudget?.countryCode,
                isoCurrencyCode: this.countryBudget?.isoCurrencyCode,
                managers: groupedBudgetEntry[1],
                isUnitValue: this.countryBudget?.isUnitValue,
                groupByFlag: groupedBudgetEntry[0]
            } as IManagersCountryBudget;
            const groupedSummaryBudget = new ManagersCountryBudget(groupedSummaryBudgetDto, this.currentConfiguration.format);
            groupedSummaryBudgets.push(groupedSummaryBudget);
        });

        this.groupedSummaryBudgets = groupedSummaryBudgets;
    }

    getGroupValues(inputFn: (budget: ManagerBudget) => string): Set<string> {
        return new Set(
            this.countryBudget?.managers
                .map(inputFn)
                .filter(value => value != null)
        );
    }

    getDepartmentNames(): Set<string> {
        return this.getGroupValues(budget => budget.department?.toLocaleLowerCase());
    }

    getWorkLocations(): Set<string> {
        return this.getGroupValues(budget => budget.workLocation?.toLocaleLowerCase());
    }

    getBusinessUnits(): Set<string> {
        return this.getGroupValues(budget => budget.businessUnit?.toLocaleLowerCase());
    }

    groupBudgetsByKey(key: string): IGroupedBudget {
        const managerBudgets = this.countryBudget ? [...this.countryBudget.managers] : [];
        return managerBudgets.reduce((acc: IGroupedBudget, budget: ManagerBudget) => {
            const matchedkeyProp = Object.entries(budget).filter(entry => entry[0] === key)[0];
            const groupingKeyValue:string = matchedkeyProp 
                ? matchedkeyProp[1] != null ? matchedkeyProp[1] : 'No Value'
                : '';
            acc[groupingKeyValue] = acc[groupingKeyValue] || [];
            acc[groupingKeyValue].push(budget);
            return acc;
        }, {});
    }
}